"use client";

import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { FundCard } from "@/components/funds/fund-card";
import { FundCategoryChips } from "@/components/funds/fund-category-chips";
import { FundSearchBar } from "@/components/funds/fund-search-bar";
import { GenerationStartedModal } from "@/components/shared/generation-started-modal";
import { Skeleton } from "@/components/ui/skeleton";

type BrowseFundReport = {
  id: string;
  slug: string;
  wtmScore: number | null;
  summaryText: string | null;
  fund: {
    schemeCode: string;
    schemeName: string;
    amc: string | null;
    category: string | null;
    fundType: string | null;
    nav: number | null;
    aum: number | null;
    expenseRatio: number | null;
    morningstarRating: number | null;
    currency: string;
  };
};

type Pagination = {
  page: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
};

export function FundsBrowseClient() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { status: authStatus } = useSession();

  const activeCategory = searchParams.get("category") ?? "";
  const activeSort = searchParams.get("sort") ?? "newest";

  const [reports, setReports] = useState<BrowseFundReport[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState<{
    schemeCode: string;
    schemeName: string;
  } | null>(null);

  const fetchReports = useCallback(
    async (category: string, sort: string, pg: number) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        params.set("sort", sort);
        params.set("page", String(pg));

        const res = await fetch(`/api/funds/browse?${params}`);
        const json = await res.json();
        if (json.success) {
          setReports(json.data.reports);
          setPagination(json.data.pagination);
          setPage(json.data.pagination.page);
        }
      } catch {
        // Browse fetch failed
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchReports(activeCategory, activeSort, 1);
  }, [activeCategory, activeSort, fetchReports]);

  function updateParams(key: string, value: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.push(`/funds?${params}`);
  }

  return (
    <>
      {/* Search bar */}
      <div className="mb-5">
        <FundSearchBar
          onSelect={(result) => {
            setSelectedFund(result);
            setModalOpen(true);
          }}
        />
        <p className="text-dim text-[11px] mt-1.5 text-center">
          🇮🇳 Currently covering Indian mutual funds. Global funds coming soon.
        </p>
      </div>

      {/* Category chips */}
      <div className="mb-4">
        <FundCategoryChips
          activeCategory={activeCategory || null}
          onSelect={(cat) => updateParams("category", cat)}
        />
      </div>

      {/* Sort tabs */}
      <div className="flex items-center gap-4 mb-5 border-b border-surface-light/20 pb-2">
        {[
          { value: "newest", label: "Newest" },
          { value: "top-rated", label: "Top Rated" },
          { value: "most-viewed", label: "Most Viewed" },
        ].map((s) => (
          <button
            key={s.value}
            onClick={() => updateParams("sort", s.value)}
            className={`text-sm pb-1 border-b-2 transition cursor-pointer bg-transparent border-x-0 border-t-0 ${
              activeSort === s.value
                ? "text-blue border-blue font-semibold"
                : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
        {pagination && (
          <span className="ml-auto text-dim text-xs">
            {pagination.totalCount} fund report{pagination.totalCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Results grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-sm mb-3">
            No fund reports found{activeCategory ? ` for "${activeCategory}"` : ""}.
          </p>
          {authStatus === "authenticated" && (
            <p className="text-dim text-xs">
              Search for a fund above to generate a new analysis.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <FundCard
              key={r.id}
              slug={r.slug}
              schemeName={r.fund.schemeName}
              amc={r.fund.amc ?? ""}
              category={r.fund.category ?? ""}
              wtmScore={r.wtmScore ?? 0}
              expenseRatio={r.fund.expenseRatio ?? 0}
              returns1Y={null}
              returns3Y={null}
              aum={r.fund.aum}
              currency={r.fund.currency}
              summaryText={r.summaryText ?? undefined}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => fetchReports(activeCategory, activeSort, page - 1)}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg text-sm bg-surface border border-surface-light/30 text-muted-foreground disabled:opacity-40 cursor-pointer disabled:cursor-default hover:border-blue/40 transition"
          >
            Previous
          </button>
          <span className="text-dim text-sm">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => fetchReports(activeCategory, activeSort, page + 1)}
            disabled={!pagination.hasMore}
            className="px-3 py-1.5 rounded-lg text-sm bg-surface border border-surface-light/30 text-muted-foreground disabled:opacity-40 cursor-pointer disabled:cursor-default hover:border-blue/40 transition"
          >
            Next
          </button>
        </div>
      )}

      {selectedFund && (
        <GenerationStartedModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          reportName={selectedFund.schemeName}
          reportType="fund"
          generatePageUrl={`/funds/generate?schemeCode=${selectedFund.schemeCode}&name=${encodeURIComponent(selectedFund.schemeName)}`}
          schemeCode={selectedFund.schemeCode}
        />
      )}
    </>
  );
}
